import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Send, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Volunteer {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  is_active: number;
}

interface HoursSummary {
  volunteer_id: number;
  first_name: string;
  last_name: string;
  total_hours: number;
  activity_count: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VolunteerRoster() {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [batchDate, setBatchDate] = useState("");
  const [batchHours, setBatchHours] = useState("");
  const [batchDesc, setBatchDesc] = useState("");

  const { data: volunteers, isLoading } = useQuery<Volunteer[]>({
    queryKey: ["volunteers", "active"],
    queryFn: async () => {
      const res = await api.get<{ data: Volunteer[] }>("/volunteers", {
        params: { activeOnly: "true" },
      });
      return res.data;
    },
  });

  const { data: hoursSummary } = useQuery<HoursSummary[]>({
    queryKey: ["volunteers", "hours-summary"],
    queryFn: async () => {
      const res = await api.get<{ data: HoursSummary[] }>(
        "/volunteers/hours-summary",
      );
      return res.data;
    },
  });

  const batchMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post("/volunteers/batch-activity", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["volunteers"] });
      queryClient.invalidateQueries({ queryKey: ["volunteer-activities"] });
      setShowBatchForm(false);
      setSelectedIds(new Set());
      setBatchDate("");
      setBatchHours("");
      setBatchDesc("");
    },
  });

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleAll() {
    if (selectedIds.size === (volunteers ?? []).length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set((volunteers ?? []).map((v) => v.id)));
    }
  }

  function handleBatchSubmit(e: React.FormEvent) {
    e.preventDefault();
    batchMutation.mutate({
      volunteerIds: Array.from(selectedIds),
      activityDate: batchDate,
      hours: parseFloat(batchHours),
      description: batchDesc || undefined,
    });
  }

  // Build hours lookup
  const hoursMap = new Map<number, HoursSummary>();
  for (const h of hoursSummary ?? []) {
    hoursMap.set(h.volunteer_id, h);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Volunteer Roster
          </h2>
          <p className="text-sm text-muted-foreground">
            Active volunteers with hours summary and batch activity logging
          </p>
        </div>
        {selectedIds.size > 0 && (
          <Button
            size="sm"
            className="gap-2"
            onClick={() => setShowBatchForm(!showBatchForm)}
          >
            <Send className="h-4 w-4" />
            Log Activity for {selectedIds.size} volunteer
            {selectedIds.size > 1 ? "s" : ""}
          </Button>
        )}
      </div>

      {showBatchForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Batch Activity Log ({selectedIds.size} volunteers)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleBatchSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input
                  label="Activity Date"
                  type="date"
                  value={batchDate}
                  onChange={(e) => setBatchDate(e.target.value)}
                  required
                />
                <Input
                  label="Hours"
                  type="number"
                  step="0.25"
                  min="0"
                  value={batchHours}
                  onChange={(e) => setBatchHours(e.target.value)}
                  required
                />
                <Input
                  label="Description"
                  value={batchDesc}
                  onChange={(e) => setBatchDesc(e.target.value)}
                  placeholder="Activity description"
                />
              </div>

              {batchMutation.isError && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {(batchMutation.error as Error)?.message ??
                    "An error occurred"}
                </div>
              )}

              <div className="flex gap-3">
                <Button type="submit" loading={batchMutation.isPending}>
                  Log for All Selected
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBatchForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      )}

      {!isLoading && (volunteers ?? []).length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No active volunteers.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && (volunteers ?? []).length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left">
                    <input
                      type="checkbox"
                      checked={
                        selectedIds.size === (volunteers ?? []).length &&
                        (volunteers ?? []).length > 0
                      }
                      onChange={toggleAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Name
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Email
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Phone
                  </th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                    Total Hours
                  </th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                    Activities
                  </th>
                </tr>
              </thead>
              <tbody>
                {(volunteers ?? []).map((vol) => {
                  const summary = hoursMap.get(vol.id);
                  return (
                    <tr
                      key={vol.id}
                      className="border-b last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(vol.id)}
                          onChange={() => toggleSelect(vol.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3 font-medium">
                        {vol.last_name}, {vol.first_name}
                      </td>
                      <td className="px-4 py-3">{vol.email ?? "--"}</td>
                      <td className="px-4 py-3">{vol.phone ?? "--"}</td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {(summary?.total_hours ?? 0).toFixed(1)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {summary?.activity_count ?? 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
